import {Button, Dropdown} from 'primereact'
import {Column} from 'primereact/column'
import {DataTable} from 'primereact/datatable'
import {ToggleButton} from 'primereact/togglebutton'
import React, {useCallback, useMemo, useRef, useState} from 'react'
import {useParams} from 'react-router-dom'
import {useAuth} from '../authentication/AuthProvider'
import Map from '../map/Map'
import {syncActivitiesData} from './ExerciseEndpoint'

import moment from 'moment-timezone'
import {Badge} from 'primereact/badge'
import {Tooltip} from 'primereact/tooltip'
import TranslateUtils from '../../i18n/TranslateUtils'
import {severityNotification} from '../notification/NotificationConstant'
import {showNotification} from '../notification/NotificationService'
import WatchDialog from '../watch-config/WatchDialog'

import {ReactComponent as DuoBeatTable} from '../../assets/icons/DuoBeatTable.svg'
import {ReactComponent as OnWristIcon} from '../../assets/watch/OnWrist.svg'
import {ReactComponent as TakeOffIcon} from '../../assets/watch/TakeOff.svg'
import {SystemRole} from '../../Constants'
import {useAppConfig} from '../../context/app/AppContext'
import {ECGDeviceTypes} from '../ecg/ECGConstants'
import StyledTitle from '../layout/StyledTitle'
import {usePageActivities} from '../observations/query-keys/ObsevationKeysManager'

const fontSize = 'text-xs sm:text-xs md:text-md lg:text-md'

const ExercisesTable = ({
    type = ECGDeviceTypes.samsung,
    isToggle = false,
    onToggleActivities = () => {},
}) => {
    //#region Translate
    const {translateCommons} = TranslateUtils()
    //#endregion

    //#region Context
    const {userRole, timezoneId} = useAppConfig()
    const {user} = useAuth()
    //#endregion

    //#region ID
    const {id: idString} = useParams()
    const id = useMemo(() => {
        if (!idString) {
            return null
        }
        return parseInt(idString)
    }, [idString])
    //#endregion

    //#region State
    const [expandedRows, setExpandedRows] = useState([])
    const [pageable, setPageable] = useState({
        rows: 25,
        pageNumber: 0,
        sortField: 'createdAt',
        sortOrder: -1,
    })

    // eslint-disable-next-line no-unused-vars
    const [filterData, setFilterData] = useState({
        userId: id,
        deviceBrand: type,
    })
    //#endregion

    //#region Ref
    const mapRef = useRef()
    const watchDialog = useRef()
    //#endregion

    //#region Query
    const {data, refetch} = usePageActivities(
        user?.id,
        pageable.pageNumber,
        pageable.rows,
        pageable.sortField,
        pageable.sortOrder,
        filterData,
        !!user?.id && !!id,
    )
    //#endregion

    //#region Memo
    const dataList = useMemo(() => {
        if (!data || !data?.content) {
            return []
        }

        return data.content
    }, [data])

    const columns = useMemo(() => {
        if (type === ECGDeviceTypes.apple) {
            return [
                {key: 'time', label: 'Time'},
                {key: 'steps', label: 'Steps'},
                {key: 'calories', label: 'Calories'},
                {key: 'sleepClassify', label: 'Sleep Duration'},
                {key: 'respiration', label: 'Respiration'},
                {key: 'heartRate', label: 'Heart Rate'},
                {key: 'oxygen', label: 'SpO2'},
                {key: 'temperature', label: 'Skin Temperature'},
                {key: 'fall', label: 'Fall'},
            ]
        }

        return [
            {key: 'time', label: 'Time'},
            {key: 'steps', label: 'Steps'},
            {key: 'calories', label: 'Calories'},
            {key: 'sleep', label: 'Sleep Duration'},
            {key: 'sleepClassify', label: 'Sleep Status'},
            {key: 'heartRate', label: 'Heart Rate'},
            {key: 'oxygen', label: 'SpO2'},
            {key: 'temperature', label: 'Skin Temperature'},
            {key: 'fall', label: 'Fall'},
            {key: 'deviceWorn', label: "Device's Worn"},
            // {key: 'seizure', label: 'Seizure'},
        ]
    }, [type])
    //#endregion

    //#region Handle Function
    const handleClick = rowData => {
        mapRef?.current.showForm(rowData)
    }

    const handleSyncData = useCallback(async () => {
        showNotification(
            severityNotification.info,
            'Synchronizing signal sent successfully !',
        )
        watchDialog?.current.showForm()
        await syncActivitiesData(id)
    }, [id])

    //#endregion

    //#region Handle Data
    const convertUtcToTimezone = (utcDateTime, timezone) => {
        try {
            const date = new Date(utcDateTime)

            const formatter = new Intl.DateTimeFormat('en-US', {
                timeZone: timezone,
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
            })

            const parts = formatter.formatToParts(date)

            const dateTimeComponents = {}
            for (const part of parts) {
                dateTimeComponents[part.type] = part.value
            }

            const formattedDate = `${dateTimeComponents.year}-${dateTimeComponents.month}-${dateTimeComponents.day} ${dateTimeComponents.hour}:${dateTimeComponents.minute}`

            return formattedDate
        } catch (error) {
            return '-'
        }
    }

    const extractAndParseDate = text => {
        if (!text) {
            return '-'
        }

        const dateTimePattern = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z/

        const match = text.match(dateTimePattern)
        if (!match) {
            return '-'
        }

        // Parse the extracted string into a Date object
        const date = moment.tz(match[0], 'YYYY-MM-DD HH:mm', timezoneId)

        if (!date?.isValid()) {
            return '-'
        }

        const formattedDateTime = convertUtcToTimezone(match[0], timezoneId)
        if (!formattedDateTime || formattedDateTime === '-') {
            return '-'
        }

        if (text?.includes('Slept at')) {
            return `${translateCommons('Slept at')} ${formattedDateTime}`
        }

        return `${translateCommons('Woke up at')} ${formattedDateTime}`
    }

    const convertSleepTime = (sleepTime, sleepClassify) => {
        if (!sleepTime || sleepTime === 0) {
            return '-'
        }

        const totalSeconds = Math.floor(sleepTime / 1000)
        const hours = Math.floor(totalSeconds / 3600)
        let minutes = Math.floor(totalSeconds / 60) - hours * 60
        const leftSeconds = totalSeconds % 60

        if (leftSeconds >= 30) {
            minutes++
        }

        return (
            <div>
                <div>
                    {hours}h {minutes}m
                </div>
                <div>
                    <small>{extractAndParseDate(sleepClassify)}</small>
                </div>
            </div>
        )
    }

    const convertSleepStatus = sleepStatus => {
        if (!sleepStatus) {
            return '-'
        }

        if (sleepStatus?.includes('Slept at')) {
            return (
                <Badge
                    style={{width: 100}}
                    value={translateCommons('Sleeping')}
                    severity="info"
                />
            )
        } else {
            return (
                <Badge
                    style={{width: 100}}
                    value={translateCommons('Awake')}
                    severity="success"
                />
            )
        }
    }

    const convertSkinTempToMBT = skinTemp => {
        if (!skinTemp) {
            return '-'
        }

        return (0.64 * 37.5 + 0.36 * skinTemp).toFixed(1)
    }

    const dynamicColumns = useMemo(() => {
        return columns.map((column, i) => {
            return (
                <Column
                    key={column.key}
                    header={
                        <label className="font-bold">
                            {translateCommons(column.label)}{' '}
                            {column?.key === 'temperature' && (
                                <span>
                                    <span className="ml-1 mr-2">
                                        <Tooltip
                                            position="top"
                                            target=".custom-skin-temp"
                                            content={translateCommons(
                                                'Normal skin temperature is between 33~37 °C',
                                            )}
                                        />
                                        <i className="custom-skin-temp pi pi-question-circle cursor-pointer"></i>
                                    </span>
                                    <span>/ MBT</span>
                                    <span className="ml-2">
                                        <Tooltip
                                            position="top"
                                            target=".custom-mbt"
                                            content={translateCommons(
                                                'Mean Body Temperature',
                                            )}
                                        />
                                        <i className="custom-mbt pi pi-question-circle cursor-pointer"></i>
                                    </span>
                                </span>
                            )}
                        </label>
                    }
                    body={rowData =>
                        column.key === 'fall' || column.key === 'seizure' ? (
                            !!rowData[column.key] ? (
                                'X'
                            ) : (
                                '-'
                            )
                        ) : column.key === 'deviceWorn' ? (
                            !!rowData[column.key] ? (
                                <span className="col-12" id="deviceWorn">
                                    <OnWristIcon
                                        style={{
                                            height: '22px',
                                            width: '22px',
                                            color: 'green',
                                        }}
                                    />
                                </span>
                            ) : (
                                <span className="col-12">
                                    <TakeOffIcon
                                        id="deviceTakeOff"
                                        style={{
                                            height: '22px',
                                            width: '22px',
                                            stroke: 'red',
                                        }}
                                    />
                                </span>
                            )
                        ) : column.key === 'sleep' ? (
                            convertSleepTime(
                                rowData[column.key],
                                rowData['sleepClassify'],
                            )
                        ) : column.key === 'sleepClassify' ? (
                            type === ECGDeviceTypes.samsung ? (
                                convertSleepStatus(rowData[column.key])
                            ) : type === ECGDeviceTypes.apple ? (
                                rowData[column.key] ?? '-'
                            ) : (
                                rowData[column.key]
                            )
                        ) : column.key === 'temperature' ? (
                            `${
                                rowData[column.key]
                                    ? `${rowData[column.key]} °C`
                                    : '-'
                            }${
                                !!rowData[column.key]
                                    ? ` / ${convertSkinTempToMBT(
                                          rowData[column.key],
                                      )} °C`
                                    : ' / -'
                            }`
                        ) : rowData[column.key] ? (
                            rowData[column.key]
                        ) : (
                            '-'
                        )
                    }
                />
            )
        })
    }, [columns, translateCommons])
    //#endregion

    //#region Render
    const actionBodyTemplate = rowData => {
        return (
            <Button
                onClick={() => handleClick(rowData)}
                type="button"
                icon="pi pi-bookmark"
                disabled={!rowData.longitude && !rowData.latitude}
            />
        )
    }

    const paginatorTemplate = useCallback(() => {
        const dropdownOptions = [
            {label: '25 records', value: 25},
            {label: '50 records', value: 50},
            {label: '100 records', value: 100},
            {label: '200 records', value: 200},
            {label: '500 records', value: 500},
        ]

        return (
            <Dropdown
                className="px-3"
                value={pageable.rows || 10}
                options={dropdownOptions}
                onChange={e => setPageable({...pageable, rows: e.value})}
            />
        )
    }, [pageable])

    const renderHeader = useCallback(() => {
        return (
            <>
                {' '}
                <div className="col-12 flex justify-content-between align-items-center p-0">
                    <StyledTitle
                        colorClass="text-blue-600"
                        icon={<DuoBeatTable />}
                        title={translateCommons('Activity Records')}
                    />
                    {/* <div className="col-4 flex justify-content-start align-items-center ">
                    <h3
                        className={`font-bold mb-0 text-md sm:text-md md:text-2xl lg:text-2xl`}>
                        <span className="mr-3">
                            <FaClimbing
                                className="font-bold text-xl"
                                style={{width: '24px', height: '24px'}}
                            />
                        </span>
                        {translateCommons('Activity Records')}
                    </h3>
                </div> */}
                    <div className="flex justify-content-end align-items-end sm:align-items-center md:align-items-center lg:align-items-center flex-column sm:flex-row md:flex-row lg:flex-row md:gap-3 lg:gap-3 sm:gap-2 gap-2 p-0 col-8">
                        <div>{paginatorTemplate()}</div>
                        {userRole !== SystemRole.USER && (
                            <div>
                                <Button
                                    className={`${fontSize}`}
                                    label={translateCommons('Sync Data')}
                                    icon="pi pi-sync"
                                    onClick={() => handleSyncData()}
                                />
                            </div>
                        )}
                        <div>
                            <ToggleButton
                                onLabel={translateCommons('Chart')}
                                offLabel={translateCommons('Table')}
                                onIcon="pi pi-chart-line"
                                offIcon="pi pi-chart-line"
                                checked={isToggle}
                                onChange={e => onToggleActivities(e.value)}
                                className={`w-9rem ${fontSize}`}
                            />
                        </div>
                    </div>
                </div>{' '}
            </>
        )
    }, [
        handleSyncData,
        isToggle,
        onToggleActivities,
        paginatorTemplate,
        translateCommons,
        userRole,
    ])

    const headerTemplate = useCallback(data => {
        return (
            <React.Fragment>
                <span className="image-text font-bold">{data.groupBy}</span>
            </React.Fragment>
        )
    }, [])
    //#endregion

    return (
        <div>
            {' '}
            {renderHeader()}
            <div className="col-12 m-0 font-bold text-center p-0 border-round clearfix border-1 overflow-hidden border-gray-300">
                <DataTable
                    value={dataList ? dataList : []}
                    rowGroupMode="subheader"
                    groupRowsBy="groupBy"
                    sortMode="single"
                    sortField="groupBy"
                    sortOrder={-1}
                    responsiveLayout="scroll"
                    expandableRowGroups
                    rowHover
                    emptyMessage={translateCommons('No results found')}
                    expandedRows={expandedRows}
                    onRowToggle={e => setExpandedRows(e.data)}
                    rowGroupHeaderTemplate={headerTemplate}
                    paginator
                    first={pageable.pageNumber * pageable.rows}
                    rows={pageable.rows}
                    onPage={e => setPageable({...pageable, pageNumber: e.page})}
                    totalRecords={dataList ? dataList.length : 0}>
                    {dynamicColumns}
                    <Column
                        header={translateCommons('Location')}
                        body={actionBodyTemplate}
                    />
                </DataTable>
                <Map ref={mapRef} />
                <WatchDialog ref={watchDialog} />
            </div>
        </div>
    )
}

export default ExercisesTable
